import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { WorkflowDataApi } from '../services/WorkflowDataApi';

const OfflineContext = createContext();

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(navigator.onLine);
  const [downloadedForms, setDownloadedForms] = useState({});
  const [offlineFormData, setOfflineFormData] = useState({});
  const [editedOfflineFormData, setEditedOfflineFormData] = useState({});
  const [offlineNotifications, setOfflineNotifications] = useState({});
  const workflowDataApi = useMemo(() => new WorkflowDataApi(), []);

  // Función para sincronizar datos offline cuando vuelve la conexión
  // Solo sincroniza formularios que fueron editados mientras se estaba offline
  const syncOfflineData = useCallback(async () => {
    // Leer datos de formularios editados offline (no los descargados)
    const data = localStorage.getItem('cenit-offline-edited-forms');
    let currentEditedData = {};
    
    if (data !== null) {
      try {
        currentEditedData = JSON.parse(data);
        // Sincronizar el estado con localStorage
        setEditedOfflineFormData((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(currentEditedData)) {
            return currentEditedData;
          }
          return prev;
        });
      } catch (error) {
        console.error('Error syncing edited offline data with storage:', error);
        return;
      }
    } else {
      // Si no hay datos en localStorage, usar el estado actual
      currentEditedData = editedOfflineFormData;
    }

    const unsyncedData = Object.entries(currentEditedData)
      .filter(([_, data]) => !data.synced && data.data && Object.keys(data.data).length > 0)
      .map(([formId, data]) => ({ formId, ...data }));

    if (!unsyncedData.length) {
      return;
    }
    // Leer formularios descargados para obtener datos de notificación y workflow
    const downloadedFormsRaw = localStorage.getItem('cenit-offline-forms');
    const downloadedFormsData = downloadedFormsRaw ? JSON.parse(downloadedFormsRaw) : {};

    const results = await Promise.allSettled(
      unsyncedData.map(item => {
        // Obtener datos del formulario descargado para construir el payload
        const downloadedForm = downloadedFormsData[item.formId] || {};
        const notification = downloadedForm.notification || null;

        // Construir la estructura requerida por el endpoint
        const payload = {
          formId: downloadedForm.data?.formId,
          id: downloadedForm.data?.id,
          notificationId: notification?.id || null,
          data: item.data,
          users: {
            nextStatusId: downloadedForm.data?.nextStatus?.id,
            notificationTo: [],
            plantId: downloadedForm.data?.plant?.id,
            contractId: null,
          }
        };

        return workflowDataApi.saveFormData(payload);
      })
    );

    // Recopilar IDs de formularios sincronizados exitosamente
    const syncedFormIds = [];

    results.forEach((result, index) => {
      const item = unsyncedData[index];

      if (result.status === "fulfilled") {
        // Eliminar formulario sincronizado del storage de editados
        delete currentEditedData[item.formId];
        syncedFormIds.push(item.formId);
      } else {
        console.error(`❌ Error al guardar el formulario ${item.formId}`, result.reason);
      }
    });

    // Actualizar storage de editados (sin los formularios ya sincronizados)
    setEditedOfflineFormData({ ...currentEditedData });
    localStorage.setItem('cenit-offline-edited-forms', JSON.stringify(currentEditedData));

    // Re-descargar en background los formularios sincronizados para tener datos actualizados
    // Esto se ejecuta sin bloquear la UI
    if (syncedFormIds.length > 0) {
      refreshSyncedForms(syncedFormIds, downloadedFormsData).catch(error => {
        console.error('Error al actualizar formularios sincronizados:', error);
      });
    }
  }, [editedOfflineFormData, workflowDataApi]);

  // Re-descarga en background los formularios sincronizados para actualizar cenit-offline-forms
  const refreshSyncedForms = async (syncedFormIds, downloadedFormsData) => {
    
    // Esperar 10 segundos para dar tiempo a que se genere la notificación
    await new Promise(resolve => setTimeout(resolve, 10000));

    try {
      // Obtener notificaciones una sola vez para todos los formularios
      let notifications = [];
      try {
        const notifResponse = await workflowDataApi.notification(false, true);
        if (notifResponse?.status === 200) {
          notifications = notifResponse.data || [];
        }
      } catch (error) {
        console.error('Error al obtener notificaciones para refresh:', error);
      }

      for (const formId of syncedFormIds) {
        try {
          const existingForm = downloadedFormsData[formId];
          if (!existingForm) continue;

          // Encontrar la notificación correspondiente para obtener externalId
          const notification = notifications.find(
            (n) => n.workflowDataId?.toString() === formId.toString()
          ) || null;

          if (!notification) {
            // Si no hay notificación, eliminar el formulario y sus datos
            const savedForms = localStorage.getItem('cenit-offline-forms');
            const currentForms = savedForms ? JSON.parse(savedForms) : {};
            
            if (currentForms[formId]) {
              delete currentForms[formId];
              setDownloadedForms({ ...currentForms });
              localStorage.setItem('cenit-offline-forms', JSON.stringify(currentForms));
            }

            // También eliminar de notificaciones si existe
            const savedNotifs = localStorage.getItem('cenit-offline-notifications');
            if (savedNotifs) {
               const currentNotifs = JSON.parse(savedNotifs);
               if (currentNotifs[formId]) {
                 delete currentNotifs[formId];
                 setOfflineNotifications({ ...currentNotifs });
                 localStorage.setItem('cenit-offline-notifications', JSON.stringify(currentNotifs));
               }
            }

            continue;
          }

          const externalId = notification?.externalId || null;

          // Determinar params para getFormData: usar externalId si existe, sino formId
          const formDataParams = externalId
            ? { externalId: externalId, id: formId }
            : { formId: existingForm.data?.formId, id: formId };

          // Obtener datos actualizados del servidor
          const [workflowInstanceRes, formDataRes] = await Promise.all([
            workflowDataApi.detailWorkflowInstance(formId),
            workflowDataApi.getFormData(formDataParams)
          ]);

          if (workflowInstanceRes?.status !== 200 || formDataRes?.status !== 200) {
            console.error(`Error al re-descargar formulario ${formId}`);
            continue;
          }

          const workflowInstance = workflowInstanceRes.data;
          const formData = formDataRes.data;

          // Descargar extra forms
          const extraFormsIds = [
            ...(workflowInstance.extraForms || []),
            ...(workflowInstance.extraPendingForms || []),
          ];
          const extraForms = [];

          for (const extraFormId of extraFormsIds) {
            if (extraFormId !== existingForm.data?.formId) {
              try {
                const extraRes = await workflowDataApi.getFormData({
                  formId: extraFormId,
                  id: formId
                });
                if (extraRes?.status === 200) {
                  extraForms.push(extraRes.data);
                }
              } catch (error) {
                console.error(`Error al descargar extra form ${extraFormId}:`, error);
              }
            }
          }

          // Usar la notificación ya encontrada arriba para actualizar datos

          // Reconstruir datos de la instancia (row) con datos actualizados
          const updatedRow = {
            ...existingForm.data,
            workflowStatus: workflowInstance.workflowStatus || existingForm.data?.workflowStatus,
            updatedAt: workflowInstance.updatedAt || existingForm.data?.updatedAt,
          };

          // Si el flujo está cerrado (statusId -2), no guardar y eliminar del storage
          if (updatedRow.workflowStatus?.id === -2) {
            const savedForms = localStorage.getItem('cenit-offline-forms');
            const currentForms = savedForms ? JSON.parse(savedForms) : {};
            delete currentForms[formId];
            setDownloadedForms({ ...currentForms });
            localStorage.setItem('cenit-offline-forms', JSON.stringify(currentForms));
            continue;
          }

          // Construir objeto actualizado para almacenar
          const dataToStore = {
            workflow: workflowInstance,
            data: updatedRow,
            formData: formData || {},
            extraForms: extraForms,
            notification: notification,
          };

          // Actualizar en cenit-offline-forms (leer directo de localStorage para evitar race conditions)
          const savedForms = localStorage.getItem('cenit-offline-forms');
          const currentForms = savedForms ? JSON.parse(savedForms) : {};
          const updatedForms = { ...currentForms, [formId]: dataToStore };
          setDownloadedForms(updatedForms);
          localStorage.setItem('cenit-offline-forms', JSON.stringify(updatedForms));

          // Actualizar notificaciones si corresponde
          if (notification) {
            const savedNotifs = localStorage.getItem('cenit-offline-notifications');
            const currentNotifs = savedNotifs ? JSON.parse(savedNotifs) : {};
            const updatedNotifs = {
              ...currentNotifs,
              [formId]: { notifications: [notification], savedAt: new Date().toISOString() }
            };
            setOfflineNotifications(updatedNotifs);
            localStorage.setItem('cenit-offline-notifications', JSON.stringify(updatedNotifs));
          }
        } catch (error) {
          console.error(`Error al re-descargar formulario ${formId}:`, error);
        }
      }
    } catch (error) {
      console.error('Error general en refreshSyncedForms:', error);
    }
  };

  // Cargar estado offline del localStorage al inicializar
  useEffect(() => {
    const savedOfflineMode = localStorage.getItem('cenit-offline-mode-enabled');
    if (savedOfflineMode === 'true') {
      setIsOfflineMode(true);
    }

    // Cargar formularios descargados
    const savedForms = localStorage.getItem('cenit-offline-forms');
    if (savedForms) {
      try {
        setDownloadedForms(JSON.parse(savedForms));
      } catch (error) {
        console.error('Error loading offline forms:', error);
      }
    }

    // Cargar datos offline
    const savedData = localStorage.getItem('cenit-offline-form-data');
    if (savedData) {
      try {
        setOfflineFormData(JSON.parse(savedData));
      } catch (error) {
        console.error('Error loading offline form data:', error);
      }
    }

    // Cargar datos de formularios editados offline
    const savedEditedData = localStorage.getItem('cenit-offline-edited-forms');
    if (savedEditedData) {
      try {
        setEditedOfflineFormData(JSON.parse(savedEditedData));
      } catch (error) {
        console.error('Error loading edited offline form data:', error);
      }
    }

    // Cargar notificaciones offline
    const savedNotifications = localStorage.getItem('cenit-offline-notifications');
    if (savedNotifications) {
      try {
        setOfflineNotifications(JSON.parse(savedNotifications));
      } catch (error) {
        console.error('Error loading offline notifications:', error);
      }
    }
  }, []);

  // Escuchar cambios en el estado de conexión
  useEffect(() => {
    const handleOnline = () => {
      setConnectionStatus(true);
      syncOfflineData();
    };

    const handleOffline = () => {
      setConnectionStatus(false);
    };

    // Verificar cuando la ventana recupera el foco
    const handleFocus = () => {
      const isOnline = navigator.onLine;
      setConnectionStatus((prev) => {
        if (prev !== isOnline) {
          if (isOnline) {
            syncOfflineData();
          }
          return isOnline;
        }
        return prev;
      });
    };

    // Llamado periódico para detectar cambios de conexión (especialmente después de recargas)
    const checkConnectionInterval = setInterval(() => {
      const isOnline = navigator.onLine;
      setConnectionStatus((prev) => {
        if (prev !== isOnline) {
          if (isOnline) {
            syncOfflineData();
          }
          return isOnline;
        }
        return prev;
      });
    }, 2000); // Verificar cada 2 segundos

    // Verificar estado inicial
    setConnectionStatus(navigator.onLine);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(checkConnectionInterval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('focus', handleFocus);
    };
  }, [syncOfflineData]); // Dependencia de syncOfflineData

  const setOfflineSidebar = (options) => {
    if (options) {
      localStorage.setItem("offline-sidebar", JSON.stringify(options))
    }
  }

  const getOfflineSidebar = () => {
    const sidebarOptions = localStorage.getItem("offline-sidebar");
    return sidebarOptions !== null ? JSON.parse(sidebarOptions) : [];
  }

  const toggleOfflineMode = () => {
    const newState = !isOfflineMode;
    setIsOfflineMode(newState);
    localStorage.setItem('cenit-offline-mode-enabled', newState.toString());
  };

  const downloadFormForOffline = (formId, formData) => {
    // Leer siempre desde localStorage para evitar race conditions
    // cuando se descargan múltiples formularios simultáneamente
    const saved = localStorage.getItem('cenit-offline-forms');
    const currentForms = saved ? JSON.parse(saved) : {};
    const newForms = {
      ...currentForms,
      [formId]: formData
    };
    setDownloadedForms(newForms);
    localStorage.setItem('cenit-offline-forms', JSON.stringify(newForms));
  };

  const isFormDownloadedForOffline = (formId) => {
    return !!downloadedForms[formId];
  };

  const saveOfflineFormData = (formId, data) => {
    // Leer siempre desde localStorage para evitar race conditions
    const saved = localStorage.getItem('cenit-offline-form-data');
    const currentData = saved ? JSON.parse(saved) : {};
    const newData = {
      ...currentData,
      [formId]: {
        data,
        savedAt: new Date().toISOString(),
        synced: false
      }
    };
    setOfflineFormData(newData);
    localStorage.setItem('cenit-offline-form-data', JSON.stringify(newData));
  };

  const getOfflineFormData = (formId) => {
    const storedFormData = localStorage.getItem('cenit-offline-form-data');
    if (storedFormData) {
      const parsedFormData = JSON.parse(storedFormData);
      return parsedFormData[formId] || null
    }
    return offlineFormData[formId] || null;
  };

  // Guardar datos de formulario editado en modo offline (storage separado)
  const saveEditedOfflineFormData = (formId, data) => {
    const saved = localStorage.getItem('cenit-offline-edited-forms');
    const currentData = saved ? JSON.parse(saved) : {};
    const newData = {
      ...currentData,
      [formId]: {
        data,
        savedAt: new Date().toISOString(),
        synced: false
      }
    };
    setEditedOfflineFormData(newData);
    localStorage.setItem('cenit-offline-edited-forms', JSON.stringify(newData));
  };

  // Obtener datos de formulario editado offline
  const getEditedOfflineFormData = (formId) => {
    const storedData = localStorage.getItem('cenit-offline-edited-forms');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      return parsedData[formId] || null;
    }
    return editedOfflineFormData[formId] || null;
  };

  /* return {
      data: {}, // detalle de la instancia
      formData: {}, // detalle del formulario asociado a la instancia
      notification: {}, // notificación actual asociada a la instancia
      workflow: {}, // detalle del flujo de trabajo
      extraForms: [] // formularios asociados, se utiliza en detalle de la instancia
    } */
  const getOfflineForm = (formId) => {
    const storedForms = localStorage.getItem('cenit-offline-forms');
    if (storedForms) {
      const parsedForms = JSON.parse(storedForms);
      return parsedForms[formId] || null
    }
    return downloadedForms[formId] || null;
  };

  const getUnsyncedFormData = () => {
    // Solo devuelve formularios editados offline (no los descargados)
    const data = localStorage.getItem('cenit-offline-edited-forms');
    if (data !== null) {
      try {
        const parsedData = JSON.parse(data);
        if (JSON.stringify(parsedData) !== JSON.stringify(editedOfflineFormData)) {
          setEditedOfflineFormData(parsedData);
        }
        return Object.entries(parsedData)
          .filter(([_, data]) => !data.synced)
          .map(([formId, data]) => ({ formId, ...data }));
      } catch (error) {
        console.error('Error parsing edited offline form data:', error);
        return [];
      }
    }
    return Object.entries(editedOfflineFormData)
      .filter(([_, data]) => !data.synced)
      .map(([formId, data]) => ({ formId, ...data }));
  };

  const markFormDataAsSynced = (formId) => {
    const newData = {
      ...editedOfflineFormData,
      [formId]: {
        ...editedOfflineFormData[formId],
        synced: true,
        syncedAt: new Date().toISOString()
      }
    };
    setEditedOfflineFormData(newData);
    localStorage.setItem('cenit-offline-edited-forms', JSON.stringify(newData));
  };

  // Función para sincronizar el estado del contexto con localStorage
  const syncOfflineDataWithStorage = () => {
    const data = localStorage.getItem('cenit-offline-form-data');
    if (data !== null) {
      try {
        const parsedData = JSON.parse(data);
        if (JSON.stringify(parsedData) !== JSON.stringify(offlineFormData)) {
          setOfflineFormData(parsedData);
        }
      } catch (error) {
        console.error('Error syncing offline data with storage:', error);
      }
    }
  };

  // Guardar notificaciones para uso offline
  const saveOfflineNotifications = (formId, notifications) => {
    const newNotifications = {
      ...offlineNotifications,
      [formId]: {
        notifications,
        savedAt: new Date().toISOString()
      }
    };
    setOfflineNotifications(newNotifications);
    localStorage.setItem('cenit-offline-notifications', JSON.stringify(newNotifications));
  };

  // Obtener notificaciones offline
  const getOfflineNotifications = (formId) => {
    const storedNotifications = localStorage.getItem('cenit-offline-notifications');
    if (storedNotifications) {
      try {
        const parsedNotifications = JSON.parse(storedNotifications);
        return parsedNotifications[formId]?.notifications || [];
      } catch (error) {
        console.error('Error loading offline notifications:', error);
        return [];
      }
    }
    return offlineNotifications[formId]?.notifications || [];
  };

  const value = useMemo(() => ({
    isOfflineMode,
    connectionStatus,
    toggleOfflineMode,
    setIsOfflineMode,
    downloadedForms,
    downloadFormForOffline,
    isFormDownloadedForOffline,
    saveOfflineFormData,
    saveEditedOfflineFormData,
    getOfflineForm,
    getOfflineFormData,
    getEditedOfflineFormData,
    getUnsyncedFormData,
    markFormDataAsSynced,
    syncOfflineData,
    syncOfflineDataWithStorage,
    setOfflineSidebar,
    getOfflineSidebar,
    saveOfflineNotifications,
    getOfflineNotifications
  }), [
    isOfflineMode,
    connectionStatus,
    downloadedForms,
    offlineFormData,
    editedOfflineFormData,
    offlineNotifications,
    syncOfflineData
  ]);

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};
